import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import ChapterMark from "@/components/brand/ChapterMark";

/**
 * National Presence — full-bleed cinematic USA map background.
 * Video loops silently; overlaid HUD, headline, gold Florida marker, and stats strip.
 */
export default function NationalPresence() {
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onReady = () => setVideoReady(true);
    v.addEventListener("loadeddata", onReady);
    // Some browsers fire 'canplay' instead
    v.addEventListener("canplay", onReady);
    return () => {
      v.removeEventListener("loadeddata", onReady);
      v.removeEventListener("canplay", onReady);
    };
  }, []);

  return (
    <section
      data-testid="national-presence-section"
      className="relative w-full overflow-hidden bg-midnight-deeper"
      style={{ minHeight: "auto" }}
    >
      {/* ── Full-bleed cinematic video background ── */}
      <div className="absolute inset-0 w-full h-full">
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
          data-testid="national-presence-video"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1600ms] ${videoReady ? "opacity-100" : "opacity-0"}`}
          style={{ filter: "saturate(0.85) contrast(1.05)" }}
        >
          <source src="/videos/sections/usa_map.webm" type="video/webm" />
          <source src="/videos/sections/usa_map.mp4" type="video/mp4" />
        </video>

        {/* Gradient overlays — bring contrast to overlay text without killing the video atmosphere */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 50% 35%, transparent 0%, rgba(10,17,40,0.35) 55%, rgba(10,17,40,0.78) 100%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,17,40,0.85) 0%, rgba(10,17,40,0.15) 18%, rgba(10,17,40,0.15) 78%, rgba(10,17,40,0.95) 100%)",
          }}
        />

        {/* Subtle film grain */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.06] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.95' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.55'/></svg>\")",
          }}
        />
      </div>

      {/* ── Foreground content ── */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12 py-32 lg:py-44 min-h-screen flex flex-col">
        {/* Top: Chapter mark */}
        <ChapterMark index="09" label="National Presence" className="mb-0" />

        {/* Center: Editorial title block */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl"
        >
          <h2 className="font-display-tight text-white text-[clamp(2.75rem,6vw,5.75rem)] leading-[0.94] tracking-[-0.045em]">
            One firm.
            <br />
            <span className="gold-metallic font-display font-light">Thirty-three states.</span>
          </h2>
          <div className="mt-10 h-px w-16 bg-gradient-to-r from-gold to-transparent" />
          <p className="mt-10 text-ag-text-secondary text-base lg:text-[1.08rem] leading-[1.75] font-light max-w-xl">
            AG Public Adjusting maintains Florida headquarters with licensed reach across thirty-three
            U.S. states — built to represent policyholders wherever the loss occurs.
          </p>
        </motion.div>

        {/* Florida marker badge — bottom-left, framed like a HUD readout */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.5 }}
          className="inline-flex items-start gap-5 max-w-md"
          data-testid="florida-hq-marker"
        >
          {/* Marker dot */}
          <div className="relative mt-1">
            <span
              className="absolute inset-0 rounded-full animate-ping"
              style={{ background: "rgba(198,168,107,0.55)" }}
            />
            <span
              className="relative block w-3 h-3 rounded-full"
              style={{
                background: "#D9CBB3",
                boxShadow: "0 0 16px rgba(217,203,179,0.9), 0 0 32px rgba(198,168,107,0.5)",
              }}
            />
          </div>
          <div>
            <div className="font-mono text-[0.6rem] text-gold tracking-[0.36em] uppercase mb-2">
              Florida Headquarters
            </div>
            <div className="font-display text-xl lg:text-2xl text-white font-light tracking-[-0.02em] leading-tight">
              30 Broadway · Kissimmee, FL
            </div>
            <div className="mt-2 font-mono text-[0.58rem] text-ag-text-muted tracking-[0.32em] uppercase">
              28.29° N · 81.41° W
            </div>
          </div>
        </motion.div>

        {/* Bottom: Stats strip */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.6 }}
          className="pt-4 sm:pt-8 lg:pt-16"
          data-testid="national-presence-stats"
        >
          <div className="border-t border-white/[0.12] pt-8 sm:pt-10 grid grid-cols-2 lg:grid-cols-4 gap-y-8 sm:gap-y-10 gap-x-6 sm:gap-x-8 lg:gap-x-16">
            {[
              { value: "33", label: "U.S. States", caption: "Licensed coverage" },
              { value: "01", label: "Florida HQ", caption: "Kissimmee, FL" },
              { value: "100%", label: "Policyholder Side", caption: "Never the carrier" },
              { value: "24/7", label: "Claims Intake", caption: "Year-round response" },
            ].map((s, i) => (
              <div key={s.label} className="relative">
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="font-mono text-[0.55rem] sm:text-[0.58rem] text-gold tracking-[0.3em] sm:tracking-[0.32em]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-1 h-px bg-gradient-to-r from-gold/30 via-white/5 to-transparent" />
                </div>
                <div className="font-display-tight gold-metallic text-[clamp(1.75rem,3.6vw,3.25rem)] leading-[0.95] tracking-[-0.04em] font-light">
                  {s.value}
                </div>
                <div className="mt-3 sm:mt-4 font-mono text-[0.58rem] sm:text-[0.62rem] text-white/85 tracking-[0.26em] sm:tracking-[0.28em] uppercase">
                  {s.label}
                </div>
                <div className="mt-2 text-ag-text-muted text-[0.72rem] sm:text-[0.78rem] leading-[1.55] font-light">
                  {s.caption}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
